import { useEffect } from 'react';

const BASE_TITLE = 'Alex Hidden Gems';
const BASE_DESCRIPTION =
"Alexandria's curated city guide — places, neighborhoods, itineraries, and local stories from the Mediterranean coast.";
export const SITE_URL = 'https://alexhiddengems.online';

export interface SeoMetaOptions {
  /** Page title (without the site name) */
  title?: string | null;
  /** Meta description */
  description?: string | null;
  /** Open Graph / Twitter image URL (absolute preferred) */
  image?: string | null;
  /** Page canonical path (e.g. /places/qaitbay-citadel) */
  path?: string | null;
  /** Language: 'en' or 'ar' */
  lang?: 'en' | 'ar';
  /** Optional Schema.org JSON-LD object (will be stringified & injected) */
  jsonLd?: Record<string, any> | Record<string, any>[] | null;
  /** Open Graph type: website, article, place, etc. */
  ogType?: string;
}

/** Get or create a meta tag with the given selector/attribute. */
function upsertMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  return el;
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

/**
 * Manages SEO meta tags and Schema.org JSON-LD for a page.
 * Cleans up the JSON-LD script on unmount so the next page can set its own.
 */
export function useSeoMeta(options: SeoMetaOptions) {
  const {
    title,
    description,
    image,
    path,
    lang = 'en',
    jsonLd,
    ogType = 'website'
  } = options;

  useEffect(() => {
    const previousTitle = document.title;
    const finalTitle = title ? `${title} · ${BASE_TITLE}` : BASE_TITLE;
    const finalDescription = description || BASE_DESCRIPTION;
    const url = path ? `${SITE_URL}${path}` : SITE_URL;
    const finalImage = image || `${SITE_URL}/og-default.jpg`;

    document.title = finalTitle;

    // Basic
    upsertMeta('name', 'description', finalDescription);
    document.documentElement.lang = lang;

    // Open Graph
    upsertMeta('property', 'og:title', finalTitle);
    upsertMeta('property', 'og:description', finalDescription);
    upsertMeta('property', 'og:type', ogType);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', finalImage);
    upsertMeta('property', 'og:site_name', BASE_TITLE);
    upsertMeta('property', 'og:locale', lang === 'ar' ? 'ar_EG' : 'en_US');

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', finalTitle);
    upsertMeta('name', 'twitter:description', finalDescription);
    upsertMeta('name', 'twitter:image', finalImage);

    // Canonical
    upsertLink('canonical', url);

    // JSON-LD
    const scriptId = 'mp-seo-jsonld';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    let jsonLdEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      jsonLdEl = document.createElement('script');
      jsonLdEl.id = scriptId;
      jsonLdEl.type = 'application/ld+json';
      jsonLdEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = previousTitle;
      if (jsonLdEl && jsonLdEl.parentNode) {
        jsonLdEl.parentNode.removeChild(jsonLdEl);
      }
    };
  }, [title, description, image, path, lang, ogType, JSON.stringify(jsonLd)]);
}

// ─────────────────────────────────────────────────────────────────────────────
// Schema.org helpers
// ─────────────────────────────────────────────────────────────────────────────

interface PlaceSchemaInput {
  id: string;
  name: {en: string;ar: string;};
  description?: {en: string;ar: string;};
  image?: string | string[];
  coordinates?: {lat: number;lng: number;};
  address?: string;
  category?: string;
  rating?: number;
  reviewCount?: number;
  priceBand?: string;
  phone?: string;
}

export function buildPlaceSchema(
place: PlaceSchemaInput,
lang: 'en' | 'ar' = 'en')
{
  const name = place.name[lang];
  const description = place.description?.[lang];
  const images = Array.isArray(place.image) ?
  place.image :
  place.image ?
  [place.image] :
  undefined;

  const schema: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'TouristAttraction',
    name,
    description,
    image: images,
    url: `${SITE_URL}/places/${place.id}`
  };

  if (place.coordinates) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      latitude: place.coordinates.lat,
      longitude: place.coordinates.lng
    };
  }

  if (place.address) {
    schema.address = {
      '@type': 'PostalAddress',
      streetAddress: place.address,
      addressLocality: 'Alexandria',
      addressCountry: 'EG'
    };
  }

  if (place.rating && place.reviewCount) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: place.rating,
      reviewCount: place.reviewCount
    };
  }

  if (place.priceBand) schema.priceRange = place.priceBand;
  if (place.phone) schema.telephone = place.phone;

  return schema;
}

interface ArticleSchemaInput {
  slug: string;
  title: {en: string;ar: string;};
  excerpt?: {en: string;ar: string;};
  image?: string;
  publishedDate?: string;
  author?: string;
}

export function buildArticleSchema(
article: ArticleSchemaInput,
lang: 'en' | 'ar' = 'en')
{
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title[lang],
    description: article.excerpt?.[lang],
    image: article.image,
    datePublished: article.publishedDate,
    author: {
      '@type': 'Organization',
      name: article.author || BASE_TITLE
    },
    publisher: {
      '@type': 'Organization',
      name: BASE_TITLE
    },
    url: `${SITE_URL}/editorial/${article.slug}`
  };
}

interface ItinerarySchemaInput {
  id: string;
  name: {en: string;ar: string;};
  description?: {en: string;ar: string;};
  image?: string;
  duration?: string;
}

export function buildItinerarySchema(
itinerary: ItinerarySchemaInput,
lang: 'en' | 'ar' = 'en')
{
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: itinerary.name[lang],
    description: itinerary.description?.[lang],
    image: itinerary.image,
    touristType: 'Cultural tourism',
    url: `${SITE_URL}/itinerary/${itinerary.id}`
  };
}

interface BreadcrumbItem {
  name: string;
  path: string;
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`
    }))
  };
}